import { type CostCenter, type CostCenter2, type ExpenseAccount, VendorTypeEnum, WorkflowConditionEnum } from "@finway-group/shared/lib/models"
import Form from "antd/lib/form"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type FilterFormInterface } from "~Components/filter/filterTypes"
import { type FormSelectOption } from "~Components/form/form.types"
import { FormSelect } from "~Components/form/formSelect"
import { SELECT_ALL_RESOURCES_OPTION } from "~Shared/config/consts"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useIsTravelEnabled } from "~Shared/hooks/featureFlags.hooks"
import { useFilterFormCreditorChange } from "~Shared/hooks/filter.hooks"
import { getTableFilterObjectStore } from "~Shared/hooks/table.hooks"
import { createDynamicList, getDynamicCostCenter2List, getDynamicCostCenterList, getDynamicExpenseAccountList } from "~Shared/utils/filter.utils"
import { getWorkFlowBasicRequestTypeLabels } from "~Shared/utils/workflow.form.utils"
import { CollapseWrapper } from "~Shared/wrapper/collapse.wrapper"
import { VendorSelect } from "../expenseForm/commonInputFields/VendorSelect"
import { WorkflowFormRequestTypeEnum } from "../workflowForm/workflowFormValues.interface"

export interface WorkflowFilter {
    requestType?: WorkflowFormRequestTypeEnum
    triggerType?: WorkflowConditionEnum
    vendor?: string
    creditorUser?: string
    costCenter?: string
    costCenter2?: string
    expenseAccount?: string
    requestedBy?: string
}

export const WorkflowFilterForm = ({ table, formInstance, options, onSubmit }: FilterFormInterface) => {
    const { t } = useTranslation()

    const tableFilterObject = getTableFilterObjectStore<WorkflowFilter>(table)
    const [workflowFilter, setWorkflowFilter] = useState(tableFilterObject)
    const { handleFinish, onCreditorChange } = useFilterFormCreditorChange(workflowFilter, setWorkflowFilter, onSubmit)
    const { showCostCentersIds } = useCompany()
    const isTravelEnabled = useIsTravelEnabled()
    const isVendorFilterSelectActive = !!workflowFilter?.vendor || !!workflowFilter?.creditorUser

    useEffect(() => {
        formInstance.resetFields()
        setWorkflowFilter(workflowFilter)
    }, [])

    useEffect(() => {
        formInstance.validateFields()
    }, [workflowFilter])

    const requestTypeList: Array<FormSelectOption> = createDynamicList(
        "label:workflow.request_type",
        isTravelEnabled ? Object.values(WorkflowFormRequestTypeEnum) : getWorkFlowBasicRequestTypeLabels(),
    )

    const triggerList: Array<FormSelectOption> = createDynamicList("label:workflow.trigger", [
        WorkflowConditionEnum.VENDOR,
        WorkflowConditionEnum.COST_CENTER,
        WorkflowConditionEnum.REQUESTER,
        WorkflowConditionEnum.NET_AMOUNT,
        WorkflowConditionEnum.COST_CENTER_2,
        WorkflowConditionEnum.EXPENSE_ACCOUNT,
    ])

    return (
        <>
            <Form id="workflowForm" className="form--filter" form={formInstance} onFinish={handleFinish}>
                <FormSelect
                    name="requestType"
                    header={t("input:workflow.request_type.title")}
                    initialValue={workflowFilter.requestType}
                    options={requestTypeList}
                    isCollapse
                    isForm
                    fallback
                />
                <FormSelect
                    name="triggerType"
                    header={t("input:workflow.trigger.trigger_type")}
                    initialValue={workflowFilter.triggerType}
                    options={triggerList}
                    isCollapse
                    fallback
                    isForm
                />
                <CollapseWrapper keyCode="" header={t("label:workflow.trigger.vendor")} isCollapse isActive={isVendorFilterSelectActive}>
                    <VendorSelect
                        shouldShowOnlyVerifiedVendors={false}
                        shouldRenderSelectDropdown={false}
                        selectedVendorType={VendorTypeEnum.VENDOR}
                        userCreditors={options.userCreditors}
                        selectedCreditorId={workflowFilter?.vendor ?? workflowFilter?.creditorUser}
                        fallback={String(t("input:filter_form.all"))}
                        fallbackValue={SELECT_ALL_RESOURCES_OPTION}
                        isForm={true}
                        formProps={{ name: "vendor", form: formInstance }}
                        onSelect={onCreditorChange}
                    />
                </CollapseWrapper>
                <FormSelect
                    name="costCenter"
                    header={t("label:workflow.trigger.cost_center")}
                    initialValue={workflowFilter.costCenter}
                    options={getDynamicCostCenterList(options.costCenters as Array<CostCenter>, showCostCentersIds)}
                    fallback={String(t("input:filter_form.all"))}
                    fallbackValue={SELECT_ALL_RESOURCES_OPTION}
                    isCollapse
                    showSearch
                    isForm
                />
                <FormSelect
                    name="requestedBy"
                    header={t("label:workflow.trigger.requester")}
                    initialValue={workflowFilter.requestedBy}
                    options={options.employees ?? []}
                    fallback={String(t("input:filter_form.all"))}
                    fallbackValue={SELECT_ALL_RESOURCES_OPTION}
                    displayImages
                    isCollapse
                    showSearch
                    isForm
                />
                <FormSelect
                    name="costCenter2"
                    header={t("label:workflow.trigger.cost_center_2")}
                    initialValue={workflowFilter.costCenter2}
                    options={getDynamicCostCenter2List(options.costCenters2 as Array<CostCenter2>)}
                    fallback={String(t("input:filter_form.all"))}
                    fallbackValue={SELECT_ALL_RESOURCES_OPTION}
                    isCollapse
                    showSearch
                    isForm
                />
                <FormSelect
                    name="expenseAccount"
                    header={t("label:workflow.trigger.expense_account")}
                    initialValue={workflowFilter.expenseAccount}
                    options={getDynamicExpenseAccountList(options.expenseAccounts as Array<ExpenseAccount>)}
                    fallback={String(t("input:filter_form.all"))}
                    fallbackValue={SELECT_ALL_RESOURCES_OPTION}
                    isCollapse
                    showSearch
                    isForm
                />
            </Form>
        </>
    )
}
